import React from "react";
import Link from "next/link";
import { buildArticleBreadcrumbs } from "lib/utils/articleBreadcrumbs";
import { ARTICLE_BORDER_COLORS, getArticleStatus } from "./constants";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";
import ArticleHeadline from "components/article/ArticleHeadline";
import type { ArticleFeedItemProps } from "./types";

export const ArticleFeedItem: React.FC<ArticleFeedItemProps> = ({
  article,
  position = 0,
}) => {
  const { track } = useMixpanelTracking();
  const articleStatus = getArticleStatus(
    article.isProContent,
    article.isSponsored
  );

  const borderColorClass = ARTICLE_BORDER_COLORS[articleStatus];
  const breadcrumbItems = buildArticleBreadcrumbs(article, {
    sectorIsLink: true,
    categoryIsLink: false,
  });

  const handleArticleClick = async () => {
    await track("Article feed item click", {
      "Article title": article.title,
      "Article URL": article.url,
      "Article ID": article.id,
      Position: position,
      Component: "FeedsSection",
    });
  };

  return (
    <div
      className={`
        group flex w-full flex-col gap-2
        border-b-[1px] ${borderColorClass}
        px-0 py-4
        transition-colors
      `}
      data-article-id={article.id}
    >
      {breadcrumbItems.length > 0 && (
        <div className="flex h-5 items-center gap-1 overflow-x-auto">
          {breadcrumbItems}
        </div>
      )}

      <Link
        href={article.url}
        aria-label={article.title}
        onClick={handleArticleClick}
        className="flex flex-col gap-2"
      >
        <ArticleHeadline
          title={article.title}
          isProContent={article.isProContent}
          className="md:line-clamp-2 md:[overflow-wrap:anywhere]"
        />
      </Link>

      {article.sponsorshipType &&
        article.sponsorDetails?.name &&
        (article.sponsorDetails.url ? (
          <Link
            href={article.sponsorDetails.url}
            className="text-brand-sifted underline transition-opacity font-emphasis-14 hover:opacity-75"
          >
            {article.sponsorshipType}: {article.sponsorDetails.name}
          </Link>
        ) : (
          <span className="text-brand-sifted font-emphasis-14">
            {article.sponsorshipType}: {article.sponsorDetails.name}
          </span>
        ))}
    </div>
  );
};

export default ArticleFeedItem;
